import React, { FunctionComponent } from 'react';
import { Grid, Button } from '@inplayer-org/inplayer-ui';
import { Formik, FormikHelpers, Field, FormikProps } from 'formik';

// utils
import { object, string } from 'yup';
import isEmpty from 'lodash/isEmpty';

// Components
import Input from 'components/Fields/Input';
import FieldError from 'components/Fields/FieldError';
import Restricted from 'restrictions/roles/components/Restricted';

// hooks
import useRestrictionRules from 'restrictions/roles/hooks/useRestrictionRules';

// constants
import Rules from 'restrictions/roles/constants/rules';

// types
import ClipBoardButton from 'components/ClipBoardButton';
import { ItalicDescription } from 'components/SharedStyledElements';
import { MdInfoOutline } from 'react-icons/md';
import { API_ROOT } from 'constants/index';
import { DescriptionContainer } from './GoogleInAppForm';
import { ButtonConfig } from '../../OvpIntegrations/components/BrightcoveForm';

const { Container, Cell } = Grid;

interface FormValues {
  secretKey: string;
}

interface OwnProps {
  onSubmit: (data: FormValues) => any;
  amazonInAppKeys: any;
  merchantUuid: string;
}

const validationSchema = object().shape({
  secretKey: string()
    .required('*Required')
    .test('secretKey', '*Required', (value) => value?.trim().length > 0),
});

type Props = OwnProps;

const AmazonInAppForm: FunctionComponent<Props> = ({ onSubmit, amazonInAppKeys, merchantUuid }) => {
  const initialValues = {
    secretKey: (amazonInAppKeys && amazonInAppKeys.value) || '',
  };

  const { hasRule } = useRestrictionRules();

  const onFormSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    await onSubmit(values);
    setSubmitting(false);
  };

  const saveButtonProps: ButtonConfig = {
    label: 'Save',
  };

  const updateButtonProps: ButtonConfig = {
    label: 'Update',
    modifier: 'buttonPrimary',
  };

  const buttonProps = !isEmpty(amazonInAppKeys) ? updateButtonProps : saveButtonProps;
  const buttonModifiers = buttonProps.modifier ? [buttonProps.modifier] : [];
  const amazonNotificationUrl = `${API_ROOT}v2/hooks/amazon-in-app/${merchantUuid}`;
  const isReadOnly = hasRule(Rules.READONLY_IN_APP_INTEGRATIONS);

  return (
    <Formik
      onSubmit={onFormSubmit}
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, isSubmitting }: FormikProps<FormValues>) => (
        <form onSubmit={handleSubmit}>
          <Container columns={3} rowGap="1rem">
            <Cell width={3}>
              <Field
                tag="text_amazon_shared_key"
                name="secretKey"
                type="text"
                component={Input}
                label="Shared Key"
                disabled={isReadOnly}
              />
              <FieldError name="secretKey" />
            </Cell>
            <Cell width={3}>
              <DescriptionContainer columns={6}>
                <Cell width={1} middle>
                  <ClipBoardButton
                    tag="roku_copy_url"
                    clipboardText={amazonNotificationUrl}
                    tooltipText="Copied!"
                    buttonModifiers={['buttonPrimary']}
                    fullWidth
                    placement="top"
                  >
                    Copy URL
                  </ClipBoardButton>
                </Cell>
                <Cell width={5} left={2}>
                  <ItalicDescription variant="p">
                    <MdInfoOutline />
                    Add this notification URL to your Amazon dashboard to connect the InPlayer and Amazon platforms.
                  </ItalicDescription>
                </Cell>
              </DescriptionContainer>
            </Cell>
            <Restricted isRestricted={isReadOnly}>
              <Cell width={1} left={3}>
                <Button
                  tag={`button_amazon_${buttonProps.label.toLowerCase()}`}
                  type="submit"
                  buttonModifiers={buttonModifiers}
                  fullWidth
                  disabled={isSubmitting}
                >
                  {buttonProps.label}
                </Button>
              </Cell>
            </Restricted>
          </Container>
        </form>
      )}
    </Formik>
  );
};

export default AmazonInAppForm;
